import { useEffect } from 'react';
import {
  CnpjFormField,
  FloatingFormItem,
  InputDecimal,
} from '@/components/_common';
import {
  useGetPaymentMethodInvoiceId,
  usePaymentMethodInvoice,
  useUpdatePaymentMethodInvoice,
} from '@/core/domain/invoice/PaymentMethod/PaymentMethod.hook';
import {
  creditCardBrandOptions,
  PaymentMethodCreateRequestDTO,
  PaymentMethodEnum,
  PaymentMethodTranslate,
} from '@/core/domain/invoice/PaymentMethod/PaymentMethod.types';
import { Col, Form, FormInstance, Input, Modal, Row, Select } from 'antd';
import { useDefaultFormErrorNotification } from '@/hooks';
import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';

interface Props {
  setPaymentId: (number: number | undefined) => void;
  paymentModalVisible: boolean;
  paymentId: number | undefined;
  setPaymentModalVisible: () => void;
  form: FormInstance<PaymentMethodCreateRequestDTO>;
  invoiceId: number;
  totaAlreadyPaid?: number;
}

const NewPaymentModal = ({
  form,
  paymentId,
  setPaymentId,
  paymentModalVisible,
  setPaymentModalVisible,
  invoiceId,
  totaAlreadyPaid = 0,
}: Props) => {
  const { mutateAsync: createMutateAsync, isLoading: isLoadingCreate } =
    usePaymentMethodInvoice();
  const { mutateAsync: updateMutateAsync, isLoading: isLoadingUpdate } =
    useUpdatePaymentMethodInvoice();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const { mutateAsync, data } = useGetPaymentMethodInvoiceId();

  const { invoiceValues } = useInvoiceFormContext();

  if (!paymentId) {
    const difference = (invoiceValues?.totalItems || 0) - totaAlreadyPaid;

    form.setFieldValue('value', difference > 0 ? difference : 0);
  }

  useEffect(() => {
    if (paymentId) mutateAsync({ invoiceId: Number(invoiceId), id: paymentId });
  }, [paymentId]);

  useEffect(() => {
    if (data) form.setFieldsValue({ ...data });
  }, [data]);

  const paymentMethodOptions = [
    { value: PaymentMethodEnum.CASH, label: PaymentMethodTranslate.CASH },
    { value: PaymentMethodEnum.CHEQUE, label: PaymentMethodTranslate.CHEQUE },
    {
      value: PaymentMethodEnum.CREDIT_CARD,
      label: PaymentMethodTranslate.CREDIT_CARD,
    },
    {
      value: PaymentMethodEnum.DEBIT_CARD,
      label: PaymentMethodTranslate.DEBIT_CARD,
    },
    {
      value: PaymentMethodEnum.STORE_CREDIT,
      label: PaymentMethodTranslate.STORE_CREDIT,
    },
    {
      value: PaymentMethodEnum.STORE_CARD,
      label: PaymentMethodTranslate.STORE_CARD,
    },
    {
      value: PaymentMethodEnum.FOOD_VOUCHER,
      label: PaymentMethodTranslate.FOOD_VOUCHER,
    },
    {
      value: PaymentMethodEnum.MEAL_VOUCHER,
      label: PaymentMethodTranslate.MEAL_VOUCHER,
    },
    {
      value: PaymentMethodEnum.GIFT_VOUCHER,
      label: PaymentMethodTranslate.GIFT_VOUCHER,
    },
    {
      value: PaymentMethodEnum.FUEL_VOUCHER,
      label: PaymentMethodTranslate.FUEL_VOUCHER,
    },
    {
      value: PaymentMethodEnum.BANK_SLIP,
      label: PaymentMethodTranslate.BANK_SLIP,
    },
    {
      value: PaymentMethodEnum.BANK_DEPOSIT,
      label: PaymentMethodTranslate.BANK_DEPOSIT,
    },
    {
      value: PaymentMethodEnum.INSTANT_PAYMENT,
      label: PaymentMethodTranslate.INSTANT_PAYMENT,
    },
    {
      value: PaymentMethodEnum.BANK_TRANSFER,
      label: PaymentMethodTranslate.BANK_TRANSFER,
    },
    {
      value: PaymentMethodEnum.LOYALTY_PROGRAM,
      label: PaymentMethodTranslate.LOYALTY_PROGRAM,
    },
    {
      value: PaymentMethodEnum.NO_PAYMENT,
      label: PaymentMethodTranslate.NO_PAYMENT,
    },
    {
      value: PaymentMethodEnum.INSTANT_PAYMENT_STATIC,
      label: PaymentMethodTranslate.INSTANT_PAYMENT_STATIC,
    },
    {
      value: PaymentMethodEnum.UNKNOWN_ELECTRONIC,
      label: PaymentMethodTranslate.UNKNOWN_ELECTRONIC,
    },
    {
      value: PaymentMethodEnum.PAYMENT_LATER,
      label: PaymentMethodTranslate.PAYMENT_LATER,
    },
    { value: PaymentMethodEnum.OTHERS, label: PaymentMethodTranslate.OTHERS },
  ];

  const intagrationTypeOptions = [
    {
      value: 1,
      label:
        'Pagamento integrado com o sistema de automação da empresa (Ex.: equipamento TEF, Comércio Eletrônico)',
    },
    {
      value: 2,
      label:
        'Pagamento não integrado com o sistema de automação da empresa (Ex.: equipamento POS)',
    },
  ];

  const handleOk = async () => {
    try {
      const formValues = await form.validateFields();

      const obj: PaymentMethodCreateRequestDTO = {
        ...formValues,
        invoiceId: Number(invoiceId),
        authorizationNumber: formValues.authorizationNumber,
        value: Number(formValues.value),
      };

      if (paymentId) {
        await updateMutateAsync({
          ...obj,
          id: paymentId,
        });
      } else {
        await createMutateAsync(obj);
      }

      setPaymentId(undefined);
      setPaymentModalVisible();
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  const paymentMethod = Form.useWatch('paymentMethod', form);

  return (
    <Modal
      confirmLoading={isLoadingUpdate || isLoadingCreate}
      width={860}
      destroyOnClose
      onOk={() => handleOk()}
      open={paymentModalVisible}
      onCancel={() => {
        setPaymentId(undefined);
        setPaymentModalVisible();
      }}
      title="Pagamento"
    >
      <Form form={form} preserve={false}>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={24} xl={18}>
            <FloatingFormItem
              name="paymentMethod"
              label="Tipo de pagamento"
              initialValue={PaymentMethodEnum.CASH}
            >
              <Select
                showSearch
                allowClear
                filterOption={false}
                options={paymentMethodOptions}
              />
            </FloatingFormItem>
          </Col>

          <Col xs={24} md={24} xl={6}>
            <FloatingFormItem label="Valor" name="value">
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          {paymentMethod === PaymentMethodEnum.OTHERS && (
            <Col span={24}>
              <FloatingFormItem
                name="descriptionPaymentMethod"
                label="Descreva o tipo de pagamento"
              >
                <Input />
              </FloatingFormItem>
            </Col>
          )}

          <Col xs={24}>
            <FloatingFormItem name="intagrationType" label="Tipo de integração">
              <Select
                showSearch
                allowClear
                filterOption={false}
                options={intagrationTypeOptions}
              />
            </FloatingFormItem>
          </Col>

          {![PaymentMethodEnum.CASH, PaymentMethodEnum.CHEQUE].includes(
            paymentMethod,
          ) && (
            <>
              <Col
                xs={24}
                md={
                  [
                    PaymentMethodEnum.CREDIT_CARD,
                    PaymentMethodEnum.DEBIT_CARD,
                  ].includes(paymentMethod)
                    ? 12
                    : 24
                }
              >
                <CnpjFormField notRequired={true} />
              </Col>
            </>
          )}

          {[
            PaymentMethodEnum.CREDIT_CARD,
            PaymentMethodEnum.DEBIT_CARD,
            PaymentMethodEnum.INSTANT_PAYMENT,
            PaymentMethodEnum.STORE_CARD,
          ].includes(paymentMethod) && (
            <>
              <Col xs={24} md={12}>
                <FloatingFormItem
                  name="creditCardBrand"
                  label="Bandeira do cartão"
                >
                  <Select
                    showSearch
                    allowClear
                    filterOption={false}
                    options={creditCardBrandOptions}
                  />
                </FloatingFormItem>
              </Col>

              <Col xs={24} md={12}>
                <FloatingFormItem
                  name="authorizationNumber"
                  label="Número autorização"
                >
                  <Input maxLength={20} />
                </FloatingFormItem>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default NewPaymentModal;
